import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PersonClient } from '../../utils/apiClient';
import { useAuth } from '../../components/Auth/AuthContext';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';

const PersonDetail = () => {
  const appContext = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [person, setPerson] = useState<any | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const personClient = new PersonClient(ApiUrl, axios);

  useEffect(() => {
    const fetchPerson = async () => {
      try {
        const personData = await personClient.getPerson(parseInt(id!));
        setPerson(personData);
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPerson();
    }
  }, [id]);

  // Проверяем, может ли пользователь редактировать эту персону
  const canEdit = () => {
    if (!user || !person) return false;
    
    // Пользователь может редактировать свой профиль или если он администратор
    return user.id === person.id || isAdmin();
  };

  if (loading) {
    return <div>Загрузка данных пользователя...</div>;
  }

  if (!person) {
    return <div>Пользователь не найден</div>;
  }

  return (
    <div className="person-detail">
      <div className="detail-header">
        <h2>{person.lastName} {person.firstName} {person.patronymic || ''}</h2>
        {canEdit() && (
          <button onClick={() => navigate(`/persons/${person.id}/edit`)} className="btn btn--edit">
            Редактировать
          </button>
        )}
      </div>

      <div className="person-info">
        <p><strong>Дата рождения:</strong> {new Date(person.birthDate).toLocaleDateString()}</p>
        <p><strong>Возраст:</strong> {new Date().getFullYear() - new Date(person.birthDate).getFullYear()} лет</p>
        <p><strong>Город:</strong> {person.city}</p>
        <p><strong>Email:</strong> {person.email}</p>

        <div className="roles">
          <h3>Роли:</h3>
          {person.personRoles && person.personRoles.length > 0 ? (
            <ul>
              {person.personRoles.map((personRole: any, index: number) => (
                <li key={index}>{personRole.role?.name}</li>
              ))}
            </ul>
          ) : (
            <p>Роли не назначены</p>
          )}
        </div>
      </div>

      {person.participants && person.participants.length > 0 && (
        <div className="participations">
          <h3>Участие в турнирах:</h3>
          <table>
            <thead>
              <tr>
                <th>Турнир</th>
                <th>Вес</th>
                <th>Спортивная школа</th>
                <th>Разряд</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {person.participants.map((participant: any) => (
                <tr key={participant.id}>
                  <td>
                    <a onClick={() => navigate(`/tournaments/${participant.tournamentId}`)} style={{ cursor: 'pointer' }}>
                      {participant.tournament?.name || 'Неизвестный турнир'}
                    </a>
                  </td>
                  <td>{participant.weight} кг</td>
                  <td>{participant.organization}</td>
                  <td>{participant.rank || 'Н/Д'}</td>
                  <td>
                    <button 
                      onClick={() => navigate(`/tournaments/${participant.tournamentId}`)}
                      className="btn btn--view"
                    >
                      Просмотр
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {person.trainedParticipants && person.trainedParticipants.length > 0 && (
        <div className="coached-participants">
          <h3>Тренируемые спортсмены:</h3>
          <table>
            <thead>
              <tr>
                <th>Спортсмен</th>
                <th>Турнир</th>
                <th>Вес</th>
                <th>Спортивная школа</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {person.trainedParticipants.map((participant: any) => (
                <tr key={participant.id}>
                  <td>
                    <a onClick={() => navigate(`/persons/${participant.personId}`)} style={{ cursor: 'pointer' }}>
                      {participant.person ?
                        `${participant.person.lastName} ${participant.person.firstName}` :
                        'Неизвестный спортсмен'}
                    </a>
                  </td>
                  <td>
                    <a onClick={() => navigate(`/tournaments/${participant.tournamentId}`)} style={{ cursor: 'pointer' }}>
                      {participant.tournament?.name || 'Неизвестный турнир'}
                    </a>
                  </td>
                  <td>{participant.weight} кг</td>
                  <td>{participant.organization}</td>
                  <td>
                    <button 
                      onClick={() => navigate(`/tournaments/${participant.tournamentId}`)}
                      className="btn btn--view"
                    >
                      Просмотр турнира
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="action-buttons">
        <button onClick={() => navigate('/persons')} className="btn btn--back">
          К списку участников
        </button>
        
        {person.personRoles?.some((pr: any) => pr.role?.name === 'Спортсмен') && (
          <button 
            onClick={() => navigate('/tournaments')} 
            className="btn btn--register"
          >
            Зарегистрироваться на турнир
          </button>
        )}
      </div>
    </div>
  );
};

export default PersonDetail;

// Дополнительные CSS стили для улучшенного отображения
const styles = `
.person-detail {
  background-color: white;
  padding: 2rem;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #4cc9f0;
  padding-bottom: 1rem;
}

.edit-button {
  background-color: #4cc9f0;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
}

.edit-button:hover {
  background-color: #3a86ff;
}

.person-info {
  margin-bottom: 2rem;
}

.roles ul {
  list-style-type: disc;
  margin-left: 1.5rem;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.back-button {
  background-color: #64748b;
  color: white;
}

.register-tournament-button {
  background-color: #10b981;
  color: white;
}

.view-button {
  background-color: #4cc9f0;
  color: white;
  padding: 0.3rem 0.6rem;
  font-size: 0.9rem;
}
`;